import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../../../components/AdminSidebar';
import AdminTopbar from '../../../components/AdminTopbar';
import { InboxAPI } from '../../../lib/api';
import ConversationView from './ConversationView';
import ComposeModal from '../../components/inbox/ComposeModal';

export default function InboxList() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [composeOpen, setComposeOpen] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        loadInbox();
    }, [debouncedSearch]);

    const loadInbox = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const params = {};
            if (debouncedSearch) params.search = debouncedSearch;

            const data = await InboxAPI.list(params);
            const list = Array.isArray(data) ? data : (data.data || []);
            setConversations(list);
        } catch (err) {
            console.error('Failed to load inbox:', err);
            setError('Failed to load inbox');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // Auto-refresh every 15 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            loadInbox(true);
        }, 15000);
        return () => clearInterval(interval);
    }, [debouncedSearch]);

    const filteredConversations = filter === 'all' ? conversations :
        filter === 'unread' ? conversations.filter(c => (c.status || 'unread') === 'unread') :
            conversations;

    if (error) {
        return (
            <div className="admin-layout min-h-screen bg-[#0f172a] text-white">
                <AdminSidebar />
                <main className="admin-content w-full px-4 sm:px-6 lg:px-8 py-6">
                    <AdminTopbar />
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="text-red-400 mb-2 font-medium">{error}</div>
                        <button
                            onClick={loadInbox}
                            className="text-sm bg-red-500/20 hover:bg-red-500/30 text-red-300 px-4 py-2 rounded-lg transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="admin-layout min-h-screen bg-[#0f172a] text-white">
            <AdminSidebar />
            <main className="admin-content w-full px-4 sm:px-6 lg:px-8 py-6">
                <AdminTopbar />

                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Inbox</h1>
                        <p className="text-gray-400 text-sm mt-1">Manage customer conversations</p>
                    </div>
                    <button
                        onClick={() => setComposeOpen(true)}
                        className="btn-primary bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                        Compose New
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-180px)]">
                    {/* Sidebar / List */}
                    <div className={`lg:col-span-1 card bg-[#1e293b] rounded-xl border border-gray-800 flex flex-col overflow-hidden ${id ? 'hidden lg:flex' : 'flex'}`}>
                        <div className="p-4 border-b border-gray-800">
                            <input
                                type="text"
                                placeholder="Search inbox..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full bg-[#0f172a] border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 outline-none focus:border-blue-500 transition-colors"
                            />
                            <div
                                className="flex gap-2 mt-3 overflow-x-auto [&::-webkit-scrollbar]:hidden"
                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                                {['all', 'unread', 'crm', 'promotions', 'starred'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`px-3 py-1 rounded-full text-xs whitespace-nowrap border transition-colors capitalize ${filter === f ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'}`}
                                    >
                                        {f}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-10">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                                    <p className="text-gray-400 text-xs">Loading...</p>
                                </div>
                            ) : filteredConversations.length === 0 ? (
                                <div className="p-4 text-center text-gray-400 text-sm">
                                    No conversations found.
                                </div>
                            ) : (
                                filteredConversations.map((conv) => {
                                    // Calculate relative time
                                    const date = new Date(conv.time);
                                    const diff = Math.floor((new Date() - date) / 1000);
                                    let timeStr = 'Just now';
                                    if (diff > 86400) timeStr = Math.floor(diff / 86400) + 'd ago';
                                    else if (diff > 3600) timeStr = Math.floor(diff / 3600) + 'h ago';
                                    else if (diff > 60) timeStr = Math.floor(diff / 60) + 'm ago';

                                    const isSelected = id === (conv.id || conv._id);
                                    const isUnread = conv.status === 'unread';

                                    return (
                                        <div
                                            key={conv.id || conv._id}
                                            onClick={() => navigate(`/admin/inbox/${conv.id || conv._id}`)}
                                            className={`p-4 border-b border-gray-800 cursor-pointer transition-all flex flex-col gap-1
                                                ${isSelected ? 'bg-blue-900/20 border-l-4 border-l-blue-500' : 'hover:bg-gray-800/50 border-l-4 border-l-transparent'}`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2">
                                                    {isUnread && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                                                    <span className={`font-semibold text-[15px] ${isUnread ? 'text-white' : 'text-gray-300'}`}>
                                                        {conv.user}
                                                    </span>
                                                </div>
                                                <span className="text-[11px] font-medium text-gray-500">{timeStr}</span>
                                            </div>
                                            <div className="flex justify-between items-center mt-0.5">
                                                <div className={`text-sm truncate pr-4 ${isUnread ? 'text-gray-200 font-medium' : 'text-gray-400'}`}>
                                                    {conv.preview || conv.subject || 'No preview available'}
                                                </div>
                                            </div>
                                            <div className="mt-1 flex items-center justify-between">
                                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">
                                                    #{String(conv.id || conv._id).substr(-4)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className={`lg:col-span-3 card bg-[#1e293b] rounded-xl border border-gray-800 overflow-hidden ${id ? 'flex' : 'hidden lg:flex'}`}>
                        {id ? (
                            <ConversationView conversationId={id} />
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-center p-8">
                                <div>
                                    <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                                        📩
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2">Select a conversation</h3>
                                    <p className="text-gray-400">Choose a message from the list to view details and reply.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <ComposeModal
                isOpen={composeOpen}
                onClose={() => setComposeOpen(false)}
                onSent={() => {
                    loadInbox();
                    setComposeOpen(false);
                }}
            />
        </div>
    );
}
